import React, { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { PATIENT } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const TYPES = ['Call', 'Fax', 'Portal Message', 'Observation', 'Check-in Response'];
const STATUSES = ['Open', 'Waiting to hear back', 'Resolved'];
const STATUS_TO_API = { 'Open': 'Open', 'Waiting to hear back': 'Awaiting Response', 'Resolved': 'Resolved' };

// Local <input type="datetime-local"> value <-> ISO string helpers.
// datetime-local emits "YYYY-MM-DDTHH:mm" in the browser's local zone.
function nowLocalForInput() {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(local) {
    if (!local) return null;
    const d = new Date(local);
    return isNaN(d) ? null : d.toISOString();
}

function isoToLocalInput(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d)) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function PatientLogForm({ patientId, initial = null, onCreated, onSaved }) {
    const editing = !!initial;
    const [entryType, setEntryType] = useState(initial?.entry_type || 'Call');
    const [note, setNote] = useState(initial?.note || '');
    const [status, setStatus] = useState(() => {
        const apiStatus = initial?.status || 'Open';
        const inverse = Object.entries(STATUS_TO_API).find(([, v]) => v === apiStatus);
        return inverse ? inverse[0] : 'Open';
    });
    const [attention, setAttention] = useState(!!initial?.attention_flag);
    const [eventAt, setEventAt] = useState(() =>
        initial?.entry_at ? isoToLocalInput(initial.entry_at) : nowLocalForInput()
    );
    const [backfilled, setBackfilled] = useState(!!initial?.is_backfilled);
    const [makeTask, setMakeTask] = useState(false);
    const [taskTitle, setTaskTitle] = useState('');
    const [taskDue, setTaskDue] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!note.trim()) { toast.error('Add a note'); return; }
        const iso = localInputToIso(eventAt) || new Date().toISOString();
        setSubmitting(true);
        try {
            if (editing) {
                await api.patch(`/patient-logs/${initial.log_id}/edit`, {
                    entry_type: entryType,
                    note,
                    status: STATUS_TO_API[status] || 'Open',
                    attention_flag: attention,
                    entry_at: iso,
                    is_backfilled: backfilled,
                });
                toast.success('Note updated');
                onSaved?.();
            } else {
                const payload = {
                    patient_id: patientId,
                    entry_type: entryType,
                    note,
                    status: STATUS_TO_API[status] || 'Open',
                    minutes_spent: 0,
                    attention_flag: attention,
                    entry_at: iso,
                    is_backfilled: backfilled,
                    linked_task: makeTask && taskTitle ? { title: taskTitle, due_date: taskDue || null } : null,
                };
                await api.post('/patient-logs', payload);
                toast.success('Note saved');
                setNote(''); setAttention(false); setMakeTask(false); setTaskTitle(''); setTaskDue('');
                setEventAt(nowLocalForInput()); setBackfilled(false);
                onCreated?.();
            }
        } catch {
            toast.error(editing ? 'Failed to update note' : 'Failed to save note');
        } finally { setSubmitting(false); }
    };

    return (
        <form onSubmit={submit} data-testid={PATIENT.logForm} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label className="text-[11px] uppercase tracking-widest text-slate-500">Type</Label>
                    <Select value={entryType} onValueChange={setEntryType}>
                        <SelectTrigger data-testid={PATIENT.logType} className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-[11px] uppercase tracking-widest text-slate-500">Status</Label>
                    <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger data-testid={PATIENT.logStatus} className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {STATUSES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div>
                <Label className="text-[11px] uppercase tracking-widest text-slate-500">
                    When did this happen?
                </Label>
                <Input
                    data-testid="patient-log-event-at"
                    type="datetime-local"
                    className="mt-1"
                    value={eventAt}
                    onChange={e => setEventAt(e.target.value)}
                />
                <div className="text-[11px] text-slate-500 mt-1">
                    Defaults to now. Set a past date/time to log something that happened earlier.
                </div>
            </div>
            <label className="flex items-center gap-2">
                <Checkbox
                    data-testid="patient-log-backfilled"
                    checked={backfilled}
                    onCheckedChange={(v) => setBackfilled(!!v)}
                />
                <span className="text-sm text-slate-700">
                    Backfilled <span className="text-slate-500">— historical entry, don’t run rules on it</span>
                </span>
            </label>
            <div>
                <Label className="text-[11px] uppercase tracking-widest text-slate-500">Note</Label>
                <Textarea
                    data-testid={PATIENT.logNote}
                    className="mt-1"
                    rows={3}
                    placeholder="What happened? (e.g., Called Dr. Park's office, left a message.)"
                    value={note}
                    onChange={e => setNote(e.target.value)}
                />
            </div>
            <label className="flex items-center gap-2">
                <Checkbox
                    data-testid={PATIENT.logAttention}
                    checked={attention}
                    onCheckedChange={(v) => setAttention(!!v)}
                />
                <span className="text-sm text-slate-700">Needs your attention</span>
            </label>
            {!editing && (
                <>
                    <label className="flex items-center gap-2 pt-2 border-t border-slate-200 mt-2">
                        <Checkbox
                            data-testid={PATIENT.logCreateTask}
                            checked={makeTask}
                            onCheckedChange={(v) => setMakeTask(!!v)}
                        />
                        <span className="text-sm text-slate-700">Add a to-do to follow up</span>
                    </label>
                    {makeTask && (
                        <div className="grid grid-cols-2 gap-3">
                            <Input
                                data-testid={PATIENT.logTaskTitle}
                                placeholder="To-do title"
                                value={taskTitle} onChange={e => setTaskTitle(e.target.value)}
                            />
                            <Input
                                data-testid={PATIENT.logTaskDue}
                                type="date" value={taskDue} onChange={e => setTaskDue(e.target.value)}
                            />
                        </div>
                    )}
                </>
            )}
            <Button
                data-testid={PATIENT.logSubmit}
                type="submit" disabled={submitting}
                className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
            >
                {submitting ? 'Saving…' : (editing ? 'Save changes' : 'Save note')}
            </Button>
        </form>
    );
}
